import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl } from 'react-intl';
import { connect } from 'react-redux';

import apiCalls from '../../utils/apiCalls';
import utils from '../../utils/utils';
import GeneralForm from '../../components/generalForm';
import CustomSelect from '../../components/customFields/customSelect';
import DATE_FORMAT from '../../components/constants/dateFormat';

function OrderBatchForm({ intl, orderBatch, deliveryDayId, onSuccess }) {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deliveryDays, setDeliveryDays] = useState([]);
  const [deliveryChange, setDeliveryChange] = useState({
    deliveryDayId,
    newDeliveryDayId: null,
    newDeliveryDayTimeRangeId: null,
  });

  useEffect(() => {
    setError(null);
    setLoading(true);
    apiCalls
      .getDeliveryDays()
      .then((result) => {
        setDeliveryDays(result.data.deliveryDays);
        setLoading(false);
      })
      .catch((errors) => {
        setError(utils.getErrorMessageFromResponse(errors));
        setLoading(false);
      });
  }, []);

  const deliveryDayOptions = deliveryDays.map((dd) => ({
    value: dd.id,
    label: `#${dd.id} - ${utils.getFormattedDateFromString(dd.deliveryDate, DATE_FORMAT.displayOnlyDate)}`,
  }));

  let timeRangeOptions = [];
  const selectedNewDeliveryDay = deliveryDays.find((dd) =>
    deliveryChange.newDeliveryDayId ? dd.id === deliveryChange.newDeliveryDayId : dd.id === deliveryDayId
  );
  if (selectedNewDeliveryDay) {
    timeRangeOptions = selectedNewDeliveryDay.timeRanges.map((tr) => ({
      value: tr.id,
      label: `#${tr.id} - ${utils.getFormattedTimeRange(tr)} ${
        tr.remaining !== null ? `(remaining: ${tr.remaining})` : ''
      }, Service area: #${tr.serviceAreaId} ${tr.serviceAreaName}`,
    }));
  }

  const formSchema = {
    type: 'object',
    required: [],
    properties: {
      deliveryDayId: {
        type: 'string',
        title: intl.formatMessage({ id: 'order.batch.form.field.delivery.day.id.label' }),
      },
      newDeliveryDayId: {
        type: 'string',
        title: intl.formatMessage({ id: 'order.batch.form.field.new.delivery.day.id.label' }),
        options: deliveryDayOptions,
      },
      newDeliveryDayTimeRangeId: {
        type: 'string',
        title: intl.formatMessage({ id: 'order.batch.form.field.new.delivery.day.time.range.id.label' }),
        options: timeRangeOptions,
      },
      overrideDeliveryDayDeadlines: {
        type: 'boolean',
        title: intl.formatMessage({ id: 'order.batch.form.field.override.deadlines' }),
      },
    },
  };

  const uiSchema = {
    deliveryDayId: {
      'ui:disabled': true,
    },
    newDeliveryDayId: {
      'ui:field': 'customSelect',
    },
    newDeliveryDayTimeRangeId: {
      'ui:field': 'customSelect',
    },
    overrideDeliveryDayDeadlines: {
      'ui:help': intl.formatMessage({ id: 'order.batch.form.field.override.deadlines.help' }),
    },
  };

  const onFormSubmit = () => {
    setLoading(true);

    apiCalls
      .editOrderBatch({
        orderBatch,
        deliveryChanges: [deliveryChange],
      })
      .then((response) => {
        onSuccess(response.data.orderBatch);
      })
      .catch((error) => {
        setError(utils.getErrorMessageFromResponse(error, true));
        setLoading(false);
        window.scrollTo(0, 0);
      });
  };

  const formFields = {
    customSelect: CustomSelect,
  };

  const buttonCopy = 'order.edit.form.submit';

  const onFormChange = (event) => {
    const newDeliveryChange = event.formData;

    if (deliveryChange.newDeliveryDayId !== newDeliveryChange.newDeliveryDayId) {
      newDeliveryChange.newDeliveryDayTimeRangeId = null;
    }

    setDeliveryChange(newDeliveryChange);
  };

  return (
    <div className="admin__order-batch-form">
      <GeneralForm
        onChange={onFormChange}
        onSubmit={onFormSubmit}
        formSchema={formSchema}
        formUiSchema={uiSchema}
        formFields={formFields}
        formData={deliveryChange}
        buttonCopy={buttonCopy}
        errorMessage={error}
        loading={loading}
        hideImageUpload={true}
      />
    </div>
  );
}

OrderBatchForm.propTypes = {
  onSuccess: PropTypes.func.isRequired,
};

OrderBatchForm.defaultProps = {
  onSuccess: () => {},
};

function mapStateToProps(state) {
  return {
    currentUser: state.currentUser.user,
  };
}

const mapDispatchToProps = {};

export default injectIntl(connect(mapStateToProps, mapDispatchToProps)(OrderBatchForm));
